if (!customElements.get('native-wizard')) {
  class NativeWizard extends HTMLElement {
    constructor() {
      super();
      this.currentStep = 0;
      this._initialized = false;
      this._rafId = null; // Track animation frames for cleanup
      this.productModule = null;

      // Pre-bind methods to ensure the reference is the same for add/removeEventListener
      this._handleInput = this._handleInput.bind(this);
    }

    connectedCallback() {
      this.productModule = this.closest("product-component");
      this._rafId = requestAnimationFrame(() => this._init());
    }

    disconnectedCallback() {
      // 1. Cancel any pending UI updates
      if (this._rafId) {
        cancelAnimationFrame(this._rafId);
      }

      // 2. Remove Event Listeners
      // Use the same reference used in addEventListener
      this.removeEventListener('change', this._handleInput);

      // 3. Clear button references to help Garbage Collection
      if (this.btnNext) this.btnNext.onclick = null;
      if (this.btnPrev) this.btnPrev.onclick = null;

      this._initialized = false;
      console.info('NativeWizard: Cleanup complete.');
    }

    _init() {
      if (this._initialized) return;

      this.steps = Array.from(this.querySelectorAll('[data-step]'));
      this.btnNext = this.querySelector('[data-action="next"]');
      this.btnPrev = this.querySelector('[data-action="prev"]');
      this.btnSubmit = this.querySelector('[data-action="submit"]');
      this.indicator = this.querySelector('[data-indicator]');
      this.descContainer = this.querySelector('[data-step-description]');
      this.summaryContainer = this.querySelector('[data-summary]');

      // Bind change event (Event Delegation)
      this.addEventListener('change', this._handleInput, { passive: true });

      if (this.btnNext) this.btnNext.onclick = () => this.navigate(1);
      if (this.btnPrev) this.btnPrev.onclick = () => this.navigate(-1);

      this._initialized = true;
      this.updateUI();
    }

    _handleInput(e) {
      if (e.target.type === 'radio') {
        // Cancel previous frame if a user clicks very fast
        if (this._rafId) cancelAnimationFrame(this._rafId);

        this._rafId = requestAnimationFrame(() => {
          this.updateValidationState();
          this.renderSummary();
          this.publishChange();
        });
      }
    }

    // Publish helper
    publishChange() {
      const summaryData = this.getSummary();
      const eventDetail = {
        detail: {
          summary: summaryData,
          currentStep: this.currentStep,
          currentStepIndex1: Number(this.currentStep) + 1,
          totalSteps: this.steps.length,
          lastChanged: summaryData.filter((i) => i.value !== null).pop(), // Get the latest choice
        },
        bubbles: true, // Allows the event to travel up the DOM tree
        composed: true, // Allows it to cross Shadow DOM boundaries if needed
      };

      // Dispatch the custom event
      this.dispatchEvent(new CustomEvent('wizard:change', eventDetail));

      // --- SUB: Subscribe to the wizard changes ---
      // document.addEventListener('wizard:change', (event) => {
      //   const data = event.detail;

      //   console.log("Wizard updated!", data);

      // Example: Updating an external element
      //   const externalHeader = document.querySelector('#external-status');
      //   if (externalHeader && data.lastChanged) {
      //     externalHeader.textContent = `Latest choice: ${data.lastChanged.value}`;
      //   }
      // });
    }

    getSummary() {
      const currentVariantId = this.productModule?.querySelector('input[type="hidden"][name="id"]')?.value;
      const currentProductUrl = window.location.pathname;
      const dyoStudioViewer = this.productModule?.querySelector('studio-viewer');
      const dyoStudioViewerId = dyoStudioViewer?.getAttribute('data-canvas-id') || null;

      return this.steps.map((step, index) => {
        const selected = step.querySelector('input[type="radio"]:checked');
        const heading = step.getAttribute('data-heading');
        const is_connected_products = step.getAttribute('data-is-connected-products') === 'true';

        const selectedValue = selected ? selected.value : null;
        const mainOption = selected?.getAttribute('data-main-option-name');
        const materialType = selected?.getAttribute('data-material-type');
        const tagSlot = selected?.getAttribute('data-tag-slot');
        const slotName = selected?.getAttribute('data-slot-name');
        const materialName = selected?.getAttribute('data-material-name');
        const isHideMaterial = selected?.getAttribute('data-is-hide-material');
        const variantItemIndex = selected?.getAttribute(
          'data-variant-item-index',
        );
        const variantSuboptionIndex = selected?.getAttribute(
          'data-variant-suboption-index',
        );

        const groupOptionName = selected?.getAttribute('data-group-option-name');
        const currentCanvasId = selected?.getAttribute('data-canvas-id') || null;

        return {
          step: index,
          stepIndex1: index + 1,
          value: selectedValue,
          materialName: materialName || null,
          mainOption: mainOption || null,
          materialType: materialType || null,
          tagSlot: tagSlot || null,
          slotName: slotName || null,
          heading: heading,
          isHideMaterial: isHideMaterial === 'true' ? true : false,
          variantItemIndex:
            variantItemIndex !== undefined ? Number(variantItemIndex) : null,
          variantSuboptionIndex:
            variantSuboptionIndex !== undefined
              ? Number(variantSuboptionIndex)
              : null,
          combinationId: currentVariantId || null,
          productUrl: currentProductUrl || null,              
          ...(is_connected_products ? this.getCurrentDyoVariant(selected) : {}),
          groupOptionName: groupOptionName || null,
          canvasId: currentCanvasId ? currentCanvasId : dyoStudioViewerId,
        };
      });
    }

    getCurrentDyoVariant(selected) {
      const dataVariantSibling = selected.parentElement.querySelector(
        '[data-variant]',
      )?.textContent
        ? JSON.parse(
            selected.parentElement.querySelector('[data-variant]')?.textContent,
          )
        : null;

      if (dataVariantSibling?.length >= 1) {
        console.warn(
          `Expected data-variant to be an array, but got: `,
          dataVariantSibling,
        );

        const dataCurrentSku = document.querySelector(
          '[data-current-sku] [data-sv-title]',
        )?.textContent;

        const currentVariantSibling = dataVariantSibling[0];
        const filteredVariantSibling = currentVariantSibling.variants.filter(
          (e) => {
            return dataCurrentSku === e.title;
          },
        );

        const variantId = filteredVariantSibling[0]?.id || null;

        return {
          productId: currentVariantSibling ? currentVariantSibling.id : null,
          sku: filteredVariantSibling[0]?.sku || null,
          title: filteredVariantSibling[0]?.title || null,
          variantId: filteredVariantSibling[0]?.id || null,
          productUrl: currentVariantSibling?.handle ? `/products/${currentVariantSibling.handle}` : null,
          productHandle: currentVariantSibling?.handle || null,
          combinationId: this.productModule?.querySelector('input[type="hidden"][name="id"]')?.value || variantId,
          options: filteredVariantSibling[0]?.options || [],
        };
      }
    }

    renderSummary() {
      if (!this.summaryContainer) return;
      const data = this.getSummary();

      // Only show items that have been selected
      const html = data
        .filter((item) => item.value !== null)
        .map(
          (item) => `
        <div class="summary-item">
          <span>${item.heading}</span>
          <span>${item.materialType}</span>
          <span>${item.tagSlot}</span>
          ${item.sku ? `<span class='hidden' data-summary-variant-sku>${item.sku}</span>` : ''}
          ${item.title ? `<span class='hidden' data-summary-variant-title>${item.title}</span>` : ''}
          ${item.variantId ? `<span class='hidden' data-summary-variant-id>${item.variantId}</span>` : ''}
          ${item.productId ? `<span class='hidden' data-summary-variant-product-id>${item.productId}</span>` : ''}
        </div>
      `,
        )
        .join('');

      this.summaryContainer.innerHTML =
        html || '<p>No selections made yet.</p>';
    }

    isValid() {
      const currentStepEl = this.steps[this.currentStep];
      if (!currentStepEl) return true;
      const radios = currentStepEl.querySelectorAll('input[type="radio"]');
      // Valid if no radios exist OR at least one is checked
      return radios.length === 0 || Array.from(radios).some((r) => r.checked);
    }

    updateValidationState() {
      const valid = this.isValid();
      if (this.btnNext) this.btnNext.disabled = !valid;

      // Toggle error class (we don't show error on the very last step)
      const isLast = this.currentStep === this.steps.length - 1;
      this.classList.toggle('has-error', !valid && !isLast);
    }

    navigate(direction) {
      const nextIndex = this.currentStep + direction;

      // Strict index bounds check
      if (nextIndex >= 0 && nextIndex < this.steps.length) {
        this.currentStep = nextIndex;

        if (this._rafId) cancelAnimationFrame(this._rafId);
        this._rafId = requestAnimationFrame(() => this.updateUI());
      }
    }

    updateUI() {
      const total = this.steps.length;
      if (total === 0) return; // Guard against empty wizard

      // 1. Update Classes
      this.steps.forEach((step, i) => {
        step.classList.toggle('is-active', i === this.currentStep);
      });

      const isFirst = this.currentStep === 0;
      const isLast = this.currentStep === total - 1;

      this.classList.toggle('is-first-step', isFirst);
      this.classList.toggle('is-last-step', isLast);

      // 2. Button Disability (Physical lock)
      if (this.btnPrev) this.btnPrev.disabled = isFirst;

      // If we are on the last step, Next should effectively be disabled
      // to prevent keyboard/script triggers
      if (this.btnNext) this.btnNext.disabled = isLast || !this.isValid();

      // 3. Update Indicator & Description
      if (this.indicator) {
        const activeStepEl = this.steps[this.currentStep];
        const optionName = activeStepEl?.getAttribute('data-heading') || '';
        this.indicator.textContent = `${optionName} ${this.currentStep + 1} / ${total}`;
      }

      if (this.descContainer) {
        const activeStepEl = this.steps[this.currentStep];
        this.descContainer.textContent =
          activeStepEl?.getAttribute('data-description') || '';
      }

      this.updateValidationState();
    }
  }

  customElements.define('native-wizard', NativeWizard);
}
